import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { UserDto, UserRoleDto } from '../../../dtos';
import { useGetUserRolesQuery } from '../../../store/generated/generatedApi';
import { emptyGuid } from '../../../util';
import {
    IFormFieldValidationConfig,
    ITransferListItem,
    createValidatorConfig,
    isNotBlank,
    isShorterThanMaxLength,
    isValidEmail,
    useStandardValidatedFormManager,
} from '../../CoreLib/library';

type FormValidationConfig<T> = Map<keyof T, IFormFieldValidationConfig>;

const USER_VALIDATION_CONFIG: FormValidationConfig<UserDto> = new Map([
    ['firstName', createValidatorConfig([isNotBlank, isShorterThanMaxLength(250)], 'First Name')],
    ['lastName', createValidatorConfig([isNotBlank, isShorterThanMaxLength(250)], 'Last Name')],
    ['email', createValidatorConfig([isNotBlank, isShorterThanMaxLength(250), isValidEmail], 'Email')],
    ['phoneNumber', createValidatorConfig([isShorterThanMaxLength(25)], 'Phone Number')],
    ['roles', createValidatorConfig([atLeastOneRole], 'Roles')],
]);

export function useUserFormManager(initialUser: UserDto) {
    const { data: allUserRoles, isLoading: isLoadingUserRoles } = useGetUserRolesQuery();
    const {
        formRecord: formUser,
        setFormRecord: setFormUser,
        isFormDirty,
        fieldErrors,
        validateForm,
        handleTextFieldChange,
        resetForm,
        validateField,
    } = useStandardValidatedFormManager(initialUser, USER_VALIDATION_CONFIG);

    const handleIsActiveChange = (_: ChangeEvent<HTMLInputElement>, isChecked: boolean) =>
        setFormUser((currentUser) => ({ ...currentUser, isActive: isChecked }));
    const handleRolesChange = useCallback(
        (updatedRoles: ITransferListItem[]) => setFormUser((currentUser) => ({ ...currentUser, roles: convertTransferListItemsToRoles(updatedRoles) })),
        [setFormUser]
    );

    const isNew = useMemo(() => formUser.id === emptyGuid, [formUser.id]);
    const currentUserRolesAsTransferListItems = useMemo(() => convertRolesToTransferListItems(formUser.roles ?? []), [formUser]);
    const allRoleOptionsAsTransferListItems = useMemo(() => convertRolesToTransferListItems(allUserRoles ?? []), [allUserRoles]);
    const isPendingInvitation = useMemo(() => !initialUser.auth0UserId, [initialUser]);
    const userDisplayName = useMemo(() => (formUser.firstName + ' ' + formUser.lastName).trim(), [formUser.firstName, formUser.lastName]);

    useEffect(() => {
        validateField('roles', formUser.roles);
    }, [validateField, formUser.roles]);

    return {
        formUser,
        isNew,
        isFormDirty,
        fieldErrors,
        validateForm,
        handleTextFieldChange,
        resetForm,
        handleRolesChange,
        currentUserRolesAsTransferListItems,
        allRoleOptionsAsTransferListItems,
        isLoadingUserRoles,
        handleIsActiveChange,
        isPendingInvitation,
        userDisplayName,
    };
}

function convertRolesToTransferListItems(items: UserRoleDto[]): ITransferListItem[] {
    return items.map((item) => ({
        label: item.name,
        value: item.id,
        description: item.description,
    }));
}

function convertTransferListItemsToRoles(items: ITransferListItem[]): UserRoleDto[] {
    return items.map((item) => ({
        id: item.value,
        name: item.label,
        description: item.description ?? '',
    }));
}

function atLeastOneRole(selectedRoles: UserRoleDto[]) {
    const isValid = selectedRoles.length > 0;
    const errorMessageBuilder = (fieldName: string) => `${fieldName} must contain at least one selection`;
    return {
        isValid,
        errorMessageBuilder,
    };
}
